import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ============================================
// Mock Data - Simulated Conversation Flow
// ============================================

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  intent?: string;
  confidence?: number;
}

const mockConversations: Message[][] = [
  // Conversation 1: Property Search Flow
  [
    { id: '1', role: 'user', content: 'Looking for beachfront villas in Canggu with pool, budget around $400k', timestamp: '10:32 AM', intent: 'property_search', confidence: 0.97 },
    { id: '2', role: 'assistant', content: 'Perfect! Canggu beachfront is our most popular zone. 🌊\n\nI found 3 villas matching your criteria:\n\n1. **Berawa Beach Villa #12**\n   💰 $385,000 | 3BR | Pool | 200m to beach\n   ROI: 13.2% | Available Now\n\n2. **Echo Beach Residence A-7**\n   💰 $420,000 | 4BR | Infinity Pool\n   ROI: 11.8% | Launching Q2\n\n3. **Pererenan Sunset Villa**\n   💰 $365,000 | 2BR | Rooftop Pool\n   ROI: 14.5% | Last Unit!\n\nWant me to show you the virtual tour for any of these?', timestamp: '10:32 AM' },
    { id: '3', role: 'user', content: 'The Pererenan one looks interesting. What\'s the rental projection?', timestamp: '10:33 AM', intent: 'continuation', confidence: 0.94 },
    { id: '4', role: 'assistant', content: '📊 **Rental Analysis - Pererenan Sunset Villa**\n\n**Nightly Rate:** $285-$420 (season dependent)\n**Avg. Occupancy:** 76% annually\n**Projected Annual Revenue:** $82,400\n\n**Operating Breakdown:**\n• Management (20%): -$16,480\n• Utilities & Maintenance: -$4,200\n• Marketing & OTAs: -$8,240\n\n**Net Annual Income: $53,480**\n**Net ROI: 14.5%** ✨\n\nThis area has the highest tourist demand. I can book you a site visit this week. Interested?', timestamp: '10:33 AM' },
  ],
  // Conversation 2: FAQ Flow
  [
    { id: '1', role: 'user', content: 'What happens if I want to sell my leasehold property before the term ends?', timestamp: '2:15 PM', intent: 'faq_question', confidence: 0.96 },
    { id: '2', role: 'assistant', content: 'Great question! Here\'s how reselling leasehold works:\n\n📋 **Reselling Your Leasehold:**\n\n1. **You can sell anytime** - No restrictions\n   • Transfer remaining lease to new buyer\n   • Market value based on years left\n\n2. **Value Calculation:**\n   • 25yr lease → sells at ~95% of new price\n   • 20yr remaining → ~85% value\n   • 15yr remaining → ~70% value\n\n3. **Transfer Process:**\n   • Notary handles deed transfer\n   • Takes 2-4 weeks typically\n   • Buyer pays transfer fees\n\n💡 **Pro tip:** Properties in prime areas often appreciate 8-12% yearly, offsetting lease depreciation.\n\nWould you like to see our resale market data?', timestamp: '2:15 PM' },
    { id: '3', role: 'user', content: 'What about taxes on rental income?', timestamp: '2:16 PM', intent: 'faq_question', confidence: 0.98 },
    { id: '4', role: 'assistant', content: '💰 **Rental Income Tax in Indonesia:**\n\n**For Foreign Investors:**\n• 20% withholding tax on gross rental\n• Paid monthly by property manager\n• No annual filing required\n\n**Tax Optimization Options:**\n\n1. **PT PMA Structure**\n   • Corporate tax rates (22%)\n   • Deduct operating expenses\n   • Better for portfolios >$500k\n\n2. **Double Tax Treaties**\n   • Many countries have agreements\n   • May reduce to 10-15%\n\n📌 We partner with international tax advisors who specialize in Bali property. Want an introduction?', timestamp: '2:16 PM' },
  ],
  // Conversation 3: Mixed Intent
  [
    { id: '1', role: 'user', content: 'Do you have any off-plan villas in Uluwatu? Also curious about the construction timeline and developer track record', timestamp: '4:45 PM', intent: 'mixed', confidence: 0.91 },
    { id: '2', role: 'assistant', content: 'Yes! Uluwatu has 2 exciting off-plan projects. Let me cover everything:\n\n🏗️ **Off-Plan Villas Available:**\n\n**1. Cliff Edge Residences**\n• Starting $520,000 | 2-3BR\n• Ocean views | Private cliff access\n• **Completion:** March 2026\n\n**2. Bukit Panorama Villas**\n• Starting $380,000 | 2BR\n• Sunset orientation | Shared infinity pool\n• **Completion:** November 2025\n\n---\n\n📋 **Construction Timeline:**\n• Foundation: Months 1-3\n• Structure: Months 4-8\n• Finishing: Months 9-14\n• Handover: Month 15\n\n---\n\n✅ **Developer Track Record:**\n• **Cliff Edge:** 12 completed projects, 8 years in Bali\n• **Bukit Panorama:** Award-winning, 6 projects delivered on-time\n\nWant me to send the full developer portfolios?', timestamp: '4:45 PM' },
    { id: '3', role: 'user', content: 'Yes please, and what\'s the payment schedule for off-plan?', timestamp: '4:46 PM', intent: 'mixed', confidence: 0.88 },
    { id: '4', role: 'assistant', content: '📄 Sending developer portfolios to your WhatsApp now!\n\n💳 **Off-Plan Payment Schedules:**\n\n**Cliff Edge (Premium):**\n• 10% reservation deposit\n• 20% on contract signing\n• 30% at structure completion\n• 30% at finishing stage\n• 10% on handover + keys\n\n**Bukit Panorama (Flexible):**\n• 15% deposit\n• 25% within 60 days\n• 25% at 50% construction\n• 25% at 80% construction\n• 10% on completion\n\n🔒 **Buyer Protection:**\n• Escrow account for all payments\n• Bank guarantee from developer\n• Independent construction audits\n\nShall I connect you with our sales team for a detailed walkthrough?', timestamp: '4:46 PM' },
  ],
];

// ============================================
// Icon Components
// ============================================
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const BotIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
  </svg>
);

const SparkleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

// ============================================
// Intent Badge Component
// ============================================
const IntentBadge: React.FC<{ intent: string; confidence: number }> = ({ intent, confidence }) => {
  const colors: Record<string, string> = {
    property_search: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    faq_question: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    general_conversation: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    continuation: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    mixed: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  };

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-medium border ${colors[intent] || colors.general_conversation}`}>
      <SparkleIcon />
      <span>{intent.replace('_', ' ')}</span>
      <span className="opacity-70">{Math.round(confidence * 100)}%</span>
    </div>
  );
};

// ============================================
// Message Bubble Component
// ============================================
const MessageBubble: React.FC<{ message: Message; showIntent?: boolean }> = ({ message, showIntent = true }) => {
  const isUser = message.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
    >
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        {/* Intent badge for user messages */}
        {isUser && message.intent && showIntent && (
          <div className="flex justify-end mb-1">
            <IntentBadge intent={message.intent} confidence={message.confidence || 0} />
          </div>
        )}

        <div className={`relative px-3 py-2 rounded-2xl ${
          isUser
            ? 'bg-[#005c4b] text-white rounded-tr-sm'
            : 'bg-[#1f2c34] text-white rounded-tl-sm'
        }`}>
          {/* Message content with markdown-like formatting */}
          <div className="text-[13px] leading-relaxed whitespace-pre-wrap">
            {message.content.split('\n').map((line, i) => {
              // Bold text
              const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              return (
                <span key={i} dangerouslySetInnerHTML={{ __html: formattedLine + (i < message.content.split('\n').length - 1 ? '<br/>' : '') }} />
              );
            })}
          </div>

          {/* Timestamp and checkmarks */}
          <div className={`flex items-center gap-1 mt-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
            <span className="text-[10px] text-white/50">{message.timestamp}</span>
            {isUser && (
              <div className="flex text-[#53bdeb]">
                <CheckIcon />
                <CheckIcon />
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// Processing Indicator
// ============================================
const ProcessingIndicator: React.FC<{ stage: string }> = ({ stage }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="flex items-start gap-2 mb-3"
  >
    <div className="w-8 h-8 bg-[#1f2c34] rounded-full flex items-center justify-center flex-shrink-0">
      <div className="text-emerald-400">
        <BotIcon />
      </div>
    </div>
    <div className="bg-[#1f2c34] rounded-2xl rounded-tl-sm px-4 py-3">
      <div className="flex items-center gap-2">
        <div className="flex gap-1">
          <motion.div
            className="w-2 h-2 bg-emerald-400 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
          />
          <motion.div
            className="w-2 h-2 bg-emerald-400 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
          />
          <motion.div
            className="w-2 h-2 bg-emerald-400 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
          />
        </div>
        <span className="text-xs text-emerald-400">{stage}</span>
      </div>
    </div>
  </motion.div>
);

// ============================================
// System Architecture Panel
// ============================================
const ArchitecturePanel: React.FC<{ currentIntent?: string }> = ({ currentIntent }) => {
  const steps = [
    { id: 'webhook', label: 'WhatsApp Webhook', icon: '📱', active: true },
    { id: 'classify', label: 'Intent Classification', icon: '🧠', active: !!currentIntent },
    { id: 'route', label: 'Smart Router', icon: '🔀', active: !!currentIntent },
    { id: 'process', label: currentIntent === 'property_search' ? 'Property Search' : currentIntent === 'faq_question' ? 'FAQ Handler' : 'Response Gen', icon: '⚡', active: !!currentIntent },
    { id: 'respond', label: 'Send Response', icon: '💬', active: !!currentIntent },
  ];

  return (
    <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-3">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 bg-blue-500/20 rounded-lg flex items-center justify-center">
          <span className="text-xs">⚙️</span>
        </div>
        <span className="text-xs font-semibold text-white">AI Pipeline</span>
      </div>

      <div className="space-y-1.5">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs transition-all ${
              step.active ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-[#161b22] border border-[#21262d]'
            }`}>
              {step.icon}
            </div>
            <div className="flex-1">
              <span className={`text-[10px] ${step.active ? 'text-white' : 'text-[#6e7681]'}`}>{step.label}</span>
            </div>
            {step.active && index < steps.length - 1 && (
              <motion.div
                className="w-1 h-1 bg-emerald-400 rounded-full"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            )}
          </div>
        ))}
      </div>

      {currentIntent && (
        <div className="mt-3 pt-3 border-t border-[#21262d]">
          <div className="text-[9px] text-[#6e7681] mb-1">Detected Intent</div>
          <IntentBadge intent={currentIntent} confidence={0.95} />
        </div>
      )}
    </div>
  );
};

// ============================================
// Quick Reply Suggestions
// ============================================
const QuickReplies: React.FC<{ onSelect: (text: string) => void }> = ({ onSelect }) => {
  const suggestions = [
    'Beachfront villas in Canggu?',
    'How does leasehold work?',
    'Off-plan payment terms?',
    'Show me Uluwatu properties',
  ];

  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {suggestions.map((text, i) => (
        <button
          key={i}
          onClick={() => onSelect(text)}
          className="px-2.5 py-1 bg-[#1f2c34] hover:bg-[#2a3942] border border-[#3b4a54] rounded-full text-[10px] text-white/80 transition-colors"
        >
          {text}
        </button>
      ))}
    </div>
  );
};

// ============================================
// Main WhatsApp Bot Demo Component
// ============================================
export const WhatsAppBotDemo: React.FC = () => {
  const [activeConversation, setActiveConversation] = useState(0);
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [inputText, setInputText] = useState('');
  const [currentIntent, setCurrentIntent] = useState<string | undefined>();

  // Simulate conversation playback
  const playConversation = async (convIndex: number) => {
    const conversation = mockConversations[convIndex];
    setDisplayedMessages([]);
    setCurrentIntent(undefined);

    for (let i = 0; i < conversation.length; i++) {
      const message = conversation[i];

      if (message.role === 'user') {
        // Show user message
        setDisplayedMessages(prev => [...prev, message]);
        setCurrentIntent(message.intent);

        // Simulate AI processing
        setIsProcessing(true);
        const stages = ['Classifying intent...', 'Routing request...', 'Generating response...'];
        for (const stage of stages) {
          setProcessingStage(stage);
          await new Promise(r => setTimeout(r, 400));
        }
        setIsProcessing(false);
      } else {
        // Show assistant message
        setDisplayedMessages(prev => [...prev, message]);
      }

      await new Promise(r => setTimeout(r, 800));
    }
  };

  // Handle conversation tab change
  const handleConversationChange = (index: number) => {
    setActiveConversation(index);
    playConversation(index);
  };

  // Handle quick reply or custom input
  const handleSendMessage = (text: string) => {
    if (!text.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      intent: text.toLowerCase().includes('villa') || text.toLowerCase().includes('property') ? 'property_search' : 'faq_question',
      confidence: 0.92,
    };

    setDisplayedMessages(prev => [...prev, newMessage]);
    setCurrentIntent(newMessage.intent);
    setInputText('');

    // Simulate response
    setIsProcessing(true);
    setTimeout(() => {
      setProcessingStage('Classifying intent...');
      setTimeout(() => {
        setProcessingStage('Searching properties...');
        setTimeout(() => {
          setProcessingStage('Generating response...');
          setTimeout(() => {
            setIsProcessing(false);
            const response: Message = {
              id: (Date.now() + 1).toString(),
              role: 'assistant',
              content: 'This is a demo showcasing how our AI customer support bot processes messages. In production, this would connect to your property database and knowledge base to provide real-time responses.',
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            };
            setDisplayedMessages(prev => [...prev, response]);
          }, 400);
        }, 400);
      }, 400);
    }, 200);
  };

  // Auto-play first conversation on mount
  useEffect(() => {
    playConversation(0);
  }, []);

  return (
    <div className="relative">
      {/* Demo Badge */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        <span className="px-2.5 py-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-full backdrop-blur-sm">
          Live Demo
        </span>
      </div>

      {/* Main Demo Container */}
      <div className="rounded-xl overflow-hidden border border-zinc-800 bg-[#0b141a]">
        <div className="flex h-[600px]">
          {/* Left Panel - WhatsApp Chat Interface */}
          <div className="flex-1 flex flex-col">
            {/* WhatsApp Header */}
            <div className="bg-[#1f2c34] px-4 py-3 flex items-center gap-3 border-b border-[#2a3942]">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center">
                <BotIcon />
              </div>
              <div className="flex-1">
                <h3 className="text-white text-sm font-medium">Bali Property Assistant</h3>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                  <span className="text-[11px] text-emerald-400">AI-powered</span>
                </div>
              </div>
              <div className="text-[#8696a0]">
                <WhatsAppIcon />
              </div>
            </div>

            {/* Conversation Tabs */}
            <div className="bg-[#111b21] px-2 py-2 flex gap-1.5 border-b border-[#2a3942]">
              {['Property Search', 'FAQ Question', 'Mixed Intent'].map((label, i) => (
                <button
                  key={i}
                  onClick={() => handleConversationChange(i)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                    activeConversation === i
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'text-[#8696a0] hover:bg-[#1f2c34]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Messages Area */}
            <div
              className="flex-1 overflow-y-auto p-4"
              style={{
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23ffffff\' fill-opacity=\'0.02\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
                backgroundColor: '#0b141a'
              }}
            >
              <AnimatePresence>
                {displayedMessages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
                {isProcessing && <ProcessingIndicator stage={processingStage} />}
              </AnimatePresence>
            </div>

            {/* Quick Replies */}
            <div className="px-3 pt-2 bg-[#0b141a]">
              <QuickReplies onSelect={handleSendMessage} />
            </div>

            {/* Input Area */}
            <div className="bg-[#1f2c34] px-3 py-2 flex items-center gap-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputText)}
                placeholder="Type a message..."
                className="flex-1 bg-[#2a3942] text-white text-sm px-4 py-2 rounded-full focus:outline-none placeholder-[#8696a0]"
              />
              <button
                onClick={() => handleSendMessage(inputText)}
                className="w-10 h-10 bg-emerald-500 hover:bg-emerald-600 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <SendIcon />
              </button>
            </div>
          </div>

          {/* Right Panel - Architecture View */}
          <div className="w-64 bg-[#0d1117] border-l border-[#21262d] p-3 flex flex-col gap-3">
            <ArchitecturePanel currentIntent={currentIntent} />

            {/* Stats */}
            <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-3">
              <div className="text-xs font-semibold text-white mb-3">Bot Performance</div>
              <div className="space-y-2">
                <div className="flex justify-between text-[10px]">
                  <span className="text-[#8b949e]">Avg Response Time</span>
                  <span className="text-emerald-400">1.2s</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-[#8b949e]">Intent Accuracy</span>
                  <span className="text-emerald-400">94.7%</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-[#8b949e]">Messages Today</span>
                  <span className="text-white">1,247</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-[#8b949e]">Handoff Rate</span>
                  <span className="text-emerald-400">8.3%</span>
                </div>
              </div>
            </div>

            {/* Tech Stack */}
            <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-3">
              <div className="text-xs font-semibold text-white mb-2">Tech Stack</div>
              <div className="flex flex-wrap gap-1">
                {['GPT-4o', 'Make.com', 'WhatsApp API', 'Airtable'].map((tech) => (
                  <span key={tech} className="px-2 py-0.5 bg-[#161b22] text-[9px] text-[#8b949e] rounded">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Bar */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-zinc-400">Interactive Demo</span>
          </div>
          <span className="text-xs text-zinc-600">|</span>
          <span className="text-xs text-zinc-500">Click tabs to see different conversation flows</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>Powered by</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">GPT-4o</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">Make.com</span>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppBotDemo;
